import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Trash2, Clock, Users, FileText, Eye, EyeOff, Edit, Download } from 'lucide-react';
import PublishExamDialog from './PublishExamDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExamManagement = () => {
  const { t } = useTranslation();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [examQuestions, setExamQuestions] = useState([]);
  const [showQuestionsDialog, setShowQuestionsDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [selectedExamForPublish, setSelectedExamForPublish] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    duration_minutes: 60,
    questions_count: 100,
    category: '',
    difficulty: ''
  });

  const [editFormData, setEditFormData] = useState({
    title: '',
    duration_minutes: 60
  });

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/exams`);
      setExams(response.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const payload = {
        title: formData.title,
        duration_minutes: parseInt(formData.duration_minutes),
        questions_count: parseInt(formData.questions_count),
        filters: {}
      };

      if (formData.category) {
        payload.filters.category = formData.category;
      }
      if (formData.difficulty) {
        payload.filters.difficulty = formData.difficulty;
      }

      await axios.post(`${API}/exams`, payload);
      toast.success(t('examCreated'));
      setShowCreateDialog(false);
      setFormData({ title: '', duration_minutes: 60, questions_count: 100, category: '', difficulty: '' });
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (examId) => {
    try {
      await axios.delete(`${API}/exams/${examId}`);
      toast.success(t('deleteSuccess'));
      fetchExams();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handlePublishToggle = async (exam) => {
    setSelectedExamForPublish(exam);
    setShowPublishDialog(true);
  };

  const handleEditExam = async (exam) => {
    try {
      // Fetch exam details with questions
      const response = await axios.get(`${API}/teacher/exams/${exam.id}/details`);
      setEditingExam(response.data.exam);
      setExamQuestions(response.data.questions);
      setEditFormData({
        title: response.data.exam.title,
        duration_minutes: response.data.exam.duration_minutes
      });
      setShowEditDialog(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleViewQuestions = () => {
    setShowEditDialog(false);
    setShowQuestionsDialog(true);
  };

  const handleRemoveQuestion = (questionId) => {
    setExamQuestions(examQuestions.filter(q => q.id !== questionId));
  };

  const handleUpdateQuestion = (questionId, field, value) => {
    setExamQuestions(examQuestions.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    ));
  };

  const handleUpdateExam = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...editFormData,
        question_ids: examQuestions.map(q => q.id)
      };
      
      // Update exam
      await axios.put(`${API}/teacher/exams/${editingExam.id}`, updateData);
      
      // Update questions
      for (const question of examQuestions) {
        await axios.put(`${API}/questions/${question.id}`, {
          question_text: question.question_text,
          option_a: question.option_a,
          option_b: question.option_b,
          option_c: question.option_c,
          option_d: question.option_d,
          correct_answer: question.correct_answer
        });
      }
      
      toast.success(t('examUpdated') || 'Exam updated successfully');
      setShowEditDialog(false);
      setShowQuestionsDialog(false);
      fetchExams();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDownloadPDF = async (examId, examTitle) => {
    try {
      // Get auth token
      const token = localStorage.getItem('token');
      
      // Fetch PDF
      const response = await fetch(`${API}/teacher/exams/${examId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }
      
      // Get blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${examTitle}.pdf`;
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(t('pdfDownloaded') || 'PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(t('error') || 'Error downloading PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exam-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">{t('exams')}</h2>
          <p className="text-gray-600 mt-1">{exams.length} {t('examsList')}</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-exam-button" className="bg-gradient-to-r from-indigo-600 to-cyan-600">
              <Plus className="h-4 w-4 mr-2" />
              {t('createExam')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('createExam')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('examTitle')}</Label>
                <Input
                  data-testid="exam-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  placeholder={t('examTitle')}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('duration')}</Label>
                <Input
                  data-testid="duration-input"
                  type="number"
                  min="1"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>{t('numberOfQuestions')}</Label>
                <Input
                  data-testid="questions-count-input"
                  type="number"
                  min="1"
                  value={formData.questions_count}
                  onChange={(e) => setFormData({...formData, questions_count: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>{t('category')} ({t('filter')})</Label>
                <Input
                  data-testid="exam-category-input"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder={t('category')}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('difficulty')} ({t('filter')})</Label>
                <Select
                  value={formData.difficulty || "all"}
                  onValueChange={(value) => setFormData({...formData, difficulty: value === "all" ? "" : value})}
                >
                  <SelectTrigger data-testid="exam-difficulty-select">
                    <SelectValue placeholder={t('difficulty')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('difficulty')}</SelectItem>
                    <SelectItem value="easy">{t('easy')}</SelectItem>
                    <SelectItem value="medium">{t('medium')}</SelectItem>
                    <SelectItem value="hard">{t('hard')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button data-testid="generate-exam-button" type="submit" disabled={creating}>
                  {creating ? t('loading') : t('generateExam')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Exams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {exams.length === 0 ? (
          <Card className="col-span-full shadow-md">
            <CardContent className="pt-6 text-center text-gray-500">
              {t('noExamsAvailable')}
            </CardContent>
          </Card>
        ) : (
          exams.map((exam, index) => (
            <Card key={exam.id} className="shadow-lg card-hover border-0" data-testid={`exam-card-${index}`}>
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-cyan-50">
                <CardTitle className="flex items-center justify-between">
                  <span className="text-lg">{exam.title}</span>
                  <div className="flex gap-2">
                    <Badge 
                      variant={exam.published ? "default" : "secondary"}
                      className={exam.published ? "bg-green-500" : "bg-yellow-500"}
                    >
                      {exam.published ? (
                        <>
                          <Eye className="h-3 w-3 mr-1" />
                          {t('published')}
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3 w-3 mr-1" />
                          {t('unpublished')}
                        </>
                      )}
                    </Badge>
                    
                    {exam.published && exam.publish_type === 'specific' && (
                      <Badge variant="outline" className="bg-blue-50">
                        <Users className="h-3 w-3 mr-1" />
                        {exam.allowed_students?.length || 0} طالب
                      </Badge>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FileText className="h-4 w-4" />
                    <span>{exam.question_ids.length} {t('questionsCount')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>{exam.duration_minutes} {t('minutes')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="h-4 w-4" />
                    <span>{new Date(exam.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t">
                  {/* Edit Button */}
                  <Button
                    onClick={() => handleEditExam(exam)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {t('edit')}
                  </Button>

                  {/* Download PDF Button */}
                  <Button
                    onClick={() => handleDownloadPDF(exam.id, exam.title)}
                    variant="outline"
                    size="sm"
                    className="w-full bg-blue-50 hover:bg-blue-100 text-blue-600"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('downloadPDF') || 'Download PDF'}
                  </Button>

                  {/* Publish/Unpublish Button */}
                  <Button
                    onClick={() => handlePublishToggle(exam)}
                    className={`w-full ${exam.published ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'}`}
                    size="sm"
                  >
                    {exam.published ? (
                      <>
                        <Users className="h-4 w-4 mr-2" />
                        إدارة النشر
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-2" />
                        {t('publishExam')}
                      </>
                    )}
                  </Button>

                  {/* Delete Button */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`delete-exam-${index}`} variant="outline" size="sm" className="text-red-600 hover:text-red-700 w-full">
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('delete')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(exam.id)}>
                          {t('delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Exam Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('editExam') || 'Edit Exam'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateExam} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">{t('examTitle')}</Label>
              <Input
                id="edit-title"
                value={editFormData.title}
                onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                placeholder={t('examTitle')}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-duration">{t('duration')} ({t('minutes')})</Label>
              <Input
                id="edit-duration"
                type="number"
                value={editFormData.duration_minutes}
                onChange={(e) => setEditFormData({...editFormData, duration_minutes: parseInt(e.target.value)})}
                min="1"
                max="300"
                required
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleViewQuestions}>
                {t('editQuestions') || 'Edit Questions'} ({examQuestions.length})
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit">
                {t('save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Questions Dialog */}
      <Dialog open={showQuestionsDialog} onOpenChange={setShowQuestionsDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('editQuestions') || 'Edit Questions'} ({examQuestions.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {examQuestions.map((question, idx) => (
              <Card key={question.id} className="border-2">
                <CardContent className="pt-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <Label className="font-bold">Q{idx + 1}</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveQuestion(question.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('question')}</Label>
                    <Input
                      value={question.question_text}
                      onChange={(e) => handleUpdateQuestion(question.id, 'question_text', e.target.value)}
                      className="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>A)</Label>
                      <Input
                        value={question.option_a}
                        onChange={(e) => handleUpdateQuestion(question.id, 'option_a', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>B)</Label>
                      <Input
                        value={question.option_b}
                        onChange={(e) => handleUpdateQuestion(question.id, 'option_b', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>C)</Label>
                      <Input
                        value={question.option_c}
                        onChange={(e) => handleUpdateQuestion(question.id, 'option_c', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>D)</Label>
                      <Input
                        value={question.option_d}
                        onChange={(e) => handleUpdateQuestion(question.id, 'option_d', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('correctAnswer')}</Label>
                    <Select 
                      value={question.correct_answer} 
                      onValueChange={(value) => handleUpdateQuestion(question.id, 'correct_answer', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4 sticky bottom-0 bg-white p-4 border-t">
            <Button variant="outline" onClick={() => setShowQuestionsDialog(false)}>
              {t('back')}
            </Button>
            <Button onClick={handleUpdateExam}>
              {t('saveChanges') || 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog */}
      <PublishExamDialog
        open={showPublishDialog}
        onOpenChange={setShowPublishDialog}
        exam={selectedExamForPublish}
        onSuccess={fetchExams}
      />
    </div>
  );
};

export default ExamManagement;
